import { Group } from "@visx/group";
import { scaleLinear } from "@visx/scale";
import { Bar } from "@visx/shape";
import { useId } from "react";

import { formatCompactWholeNumberWithUnit } from "@/web/lib/formatting";

const CHART_WIDTH = 340;
const CHART_HEIGHT = 280;
const MARGIN = { top: 8, bottom: 8, left: 8 };

/** Bar (BAR_WIDTH) + bracket gap + caption column. Used to horizontally center
 * the whole drawing within CHART_WIDTH so it isn't pinned to the left edge. */
const BRACKET_GAP = 16;
const CAPTION_COLUMN = 150;

/**
 * Single column showing how the estimated purchase price is (or will be)
 * funded. Stacks four segments from the bottom up:
 *   1. your contribution (tenant down payment)
 *   2. seller financing / retained portion
 *   3. community investor interest
 *   4. the remaining gap you still need to fundraise (muted, on top)
 *
 * Our designer put each value label *inside* its own segment, so a tiny
 * segment would clip its text. To keep every figure legible we give each
 * rendered segment a minimum pixel height of one text line. That makes the
 * chart deliberately NOT to scale in lopsided states (e.g. a $20 contribution
 * next to a $1M gap) — legibility wins over proportionality here.
 */

const BAR_WIDTH = 130;
/** Minimum height for any segment we actually draw, so its in-bar label fits. */
const MIN_SEGMENT_PX = 30;

type SegmentKey = "contribution" | "seller" | "community" | "gap";

export type FundraiseStackedChartProps = {
  /** Estimated purchase price — the whole bar. `undefined` → TBD axis. */
  estimatedPrice: number | undefined;
  /** Tenant's intended contribution (down payment). `undefined` → TBD. */
  contribution: number | undefined;
  /** Seller financing / retained portion. `undefined` → not yet known. */
  sellerFinancing: number | undefined;
  /** Sum of community expressions of interest. Always defined (0 when none). */
  communityInterest: number;
};

type Segment = {
  key: SegmentKey;
  /** Raw dollar value used for proportional sizing (0 when TBD/empty). */
  value: number;
  /** Text shown inside the segment (formatted dollars, or a placeholder). */
  valueLabel: string;
  /** Small caption to the right of the bar describing the segment. */
  caption: string;
  fill: string;
  /** Light segments need dark text; dark segments need light text. */
  textColor: string;
  /** When true, this is an empty/placeholder segment (e.g. "No interest yet"). */
  muted: boolean;
};

/**
 * `--withus-blue` at a given alpha. We tint via opacity (relative-color oklch)
 * rather than `color-mix(... white)` because mixing this oklch blue toward
 * white in sRGB desaturates it toward grey — opacity keeps the hue intact
 * against the yellow drawer background.
 */
function blueAlpha(alpha: number): string {
  return `oklch(from var(--withus-blue) l c h / ${alpha})`;
}

function formatDollars(value: number): string {
  return formatCompactWholeNumberWithUnit(
    Intl.NumberFormat("en-US").format(Math.round(value)),
    "Dollars",
  );
}

export function FundraiseStackedChart({
  estimatedPrice,
  contribution,
  sellerFinancing,
  communityInterest,
}: FundraiseStackedChartProps) {
  // Per-instance id so multiple charts on one page don't collide on the shared
  // `<pattern>` id (browsers would resolve `url(#...)` to the first match).
  const hatchId = useId();
  const contributionValue = contribution ?? 0;
  const sellerValue = sellerFinancing ?? 0;
  const communityValue = communityInterest;

  // The committed total never exceeds the estimated price on screen: if a user
  // has (somehow) committed more than the estimate, we still fill the whole
  // bar and show a zero gap rather than overflowing the axis.
  const committed = contributionValue + sellerValue + communityValue;
  const priceKnown = estimatedPrice !== undefined && estimatedPrice > 0;
  // When we don't have a price estimate yet, fall back to the committed total
  // (or a nominal floor) so the bar still renders something sensible.
  const barTotal = priceKnown ? Math.max(estimatedPrice, committed) : committed > 0 ? committed : 1;
  const gapValue = priceKnown ? Math.max(0, estimatedPrice - committed) : 0;

  const segments: Segment[] = [
    {
      key: "contribution",
      value: contributionValue,
      valueLabel: contribution !== undefined ? formatDollars(contribution) : "TBD",
      caption: "Your contribution",
      fill: blueAlpha(0.9),
      textColor: "var(--withus-yellow)",
      muted: contribution === undefined,
    },
    {
      key: "seller",
      value: sellerValue,
      valueLabel: sellerFinancing !== undefined ? formatDollars(sellerFinancing) : "TBD",
      caption: "Seller contribution",
      fill: blueAlpha(0.8),
      textColor: "var(--withus-yellow)",
      muted: sellerFinancing === undefined,
    },
    {
      key: "community",
      value: communityValue,
      valueLabel: communityValue > 0 ? formatDollars(communityValue) : "No interest yet",
      caption: "Investor interest",
      fill: blueAlpha(0.7),
      textColor: "var(--withus-yellow)",
      muted: communityValue <= 0,
    },
    {
      key: "gap",
      value: gapValue,
      valueLabel: priceKnown ? formatDollars(gapValue) : "TBD",
      caption: "Amount you'll need to fundraise",
      fill: blueAlpha(0.6),
      textColor: "var(--withus-yellow)",
      muted: !priceKnown || gapValue <= 0,
    },
  ];

  const innerHeight = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

  const yScale = scaleLinear<number>({
    domain: [0, barTotal],
    range: [0, innerHeight],
  });

  // Layout: the three contributor segments (contribution/seller/community) are
  // kept compact and the gap ("amount you'll need to fundraise") expands to
  // fill whatever vertical space is left. Per design, the bottom three only
  // ever shrink down to a one-line minimum; the top segment owns the slack.
  //
  // We always render the three contributor rows (so each party shows even at
  // $0 / TBD); the gap row is dropped only when the deal is fully funded.
  type LaidOutSegment = Segment & { height: number };
  const showGap = gapValue > 0 || !priceKnown;
  const contributorSegments = segments.filter((segment) => segment.key !== "gap");
  const gapSegment = showGap ? segments.find((segment) => segment.key === "gap") : undefined;

  // Natural (to-scale) height for each contributor, floored at the one-line min.
  const contributorNatural = contributorSegments.map((segment) => ({
    ...segment,
    height: Math.max(yScale(segment.value), MIN_SEGMENT_PX),
  }));
  const contributorNaturalTotal = contributorNatural.reduce((acc, s) => acc + s.height, 0);

  // Reserve at least a one-line slot for the gap when it's shown, then let the
  // contributors take their natural height out of the remainder. If they don't
  // fit (their natural total already exceeds the space left for them), compress
  // them proportionally — but never below the one-line minimum each.
  const reservedForGap = gapSegment ? MIN_SEGMENT_PX : 0;
  const spaceForContributors = innerHeight - reservedForGap;
  const minContributorTotal = contributorNatural.length * MIN_SEGMENT_PX;

  let contributorHeights: LaidOutSegment[];
  if (contributorNaturalTotal <= spaceForContributors) {
    // Everything fits: contributors keep their (floored) natural heights and
    // the gap soaks up the rest.
    contributorHeights = contributorNatural;
  } else if (spaceForContributors <= minContributorTotal) {
    // Not even the minimums fit alongside a full-size gap: pin every
    // contributor to its minimum and let the gap take whatever is left.
    contributorHeights = contributorNatural.map((s) => ({ ...s, height: MIN_SEGMENT_PX }));
  } else {
    // Compress the slack above each contributor's minimum proportionally so the
    // three together fit `spaceForContributors`.
    const slack = spaceForContributors - minContributorTotal;
    const naturalSlack = contributorNaturalTotal - minContributorTotal;
    const ratio = naturalSlack > 0 ? slack / naturalSlack : 0;
    contributorHeights = contributorNatural.map((s) => ({
      ...s,
      height: MIN_SEGMENT_PX + (s.height - MIN_SEGMENT_PX) * ratio,
    }));
  }

  const usedByContributors = contributorHeights.reduce((acc, s) => acc + s.height, 0);
  const normalized: LaidOutSegment[] = [
    ...contributorHeights,
    ...(gapSegment
      ? [{ ...gapSegment, height: Math.max(MIN_SEGMENT_PX, innerHeight - usedByContributors) }]
      : []),
  ];

  // Center the whole drawing (bar + caption column) within CHART_WIDTH so it
  // sits in the middle of its container instead of hugging the left edge.
  const contentWidth = BAR_WIDTH + BRACKET_GAP + CAPTION_COLUMN;
  const barX = Math.max(MARGIN.left, (CHART_WIDTH - contentWidth) / 2);

  // Walk from the bottom of the inner area upward.
  let cursorY = MARGIN.top + innerHeight;
  const placed = normalized.map((segment) => {
    const height = segment.height;
    const y = cursorY - height;
    cursorY = y;
    return { ...segment, y, height };
  });

  const axisX = barX + BAR_WIDTH + 8;

  // Ticks at every segment boundary: the top edge of each placed segment plus
  // the very bottom of the bar. De-duped so shared edges don't double-draw.
  const barTopY = MARGIN.top;
  const barBottomY = MARGIN.top + innerHeight;
  const tickYs = Array.from(new Set([...placed.map((segment) => segment.y), barBottomY]));

  return (
    <svg
      role="img"
      aria-label="How your purchase is funded"
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      className="h-auto w-full overflow-visible"
    >
      <defs>
        {/* Diagonal hatch for empty/placeholder segments (e.g. "No interest yet"). */}
        <pattern
          id={hatchId}
          width={8}
          height={8}
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(-60)"
        >
          <rect width={8} height={8} fill="transparent" />
          <line
            x1={0}
            y1={0}
            x2={0}
            y2={8}
            stroke="var(--withus-blue)"
            strokeOpacity={0.2}
            strokeWidth={3}
          />
        </pattern>
      </defs>

      {/* Boundary ticks: one short horizontal tick at each segment edge. A 1px
          stroke straddles its y by ±0.5px, so the extreme ticks (bar top/bottom)
          would paint half a pixel *outside* the bar. Nudge those two inward by
          0.5px so they sit flush with the bar's edge. */}
      {tickYs.map((tickY) => {
        const y = tickY === barTopY ? tickY + 0.5 : tickY === barBottomY ? tickY - 0.5 : tickY;
        return (
          <line
            key={tickY}
            x1={barX + BAR_WIDTH}
            x2={axisX}
            y1={y}
            y2={y}
            stroke="var(--withus-dark-blue)"
            strokeOpacity={0.4}
          />
        );
      })}

      {placed.map((segment) => (
        <Group key={segment.key}>
          <Bar
            x={barX}
            y={segment.y}
            width={BAR_WIDTH}
            height={segment.height}
            fill={segment.fill}
          />
          {segment.muted && segment.value <= 0 && (
            <Bar
              x={barX}
              y={segment.y}
              width={BAR_WIDTH}
              height={segment.height}
              fill={`url(#${hatchId})`}
            />
          )}
          {/* Value label near the top of the segment, left-aligned in the bar. */}
          <text
            x={barX + 10}
            y={segment.y + 16}
            className="text-[0.8125rem] font-semibold"
            fill={segment.textColor}
          >
            {segment.valueLabel}
          </text>

          {/* Caption aligned to the vertical CENTER of the segment. Clamped to
              one line — overflow is truncated rather than wrapped. */}
          <CaptionText
            x={axisX + 8}
            y={segment.y + segment.height / 2}
            text={segment.caption}
            maxWidth={CAPTION_COLUMN}
            allowWrap={segment.key === "gap"}
          />
        </Group>
      ))}
    </svg>
  );
}

const CAPTION_LINE_HEIGHT = 13;

/**
 * Caption centered vertically on `y`. Short captions stay on one line; only
 * the long "amount you'll need to fundraise" caption is allowed to wrap (by
 * passing `allowWrap`). When wrapping, the block is balanced across `maxWidth`
 * and re-centered on `y`.
 */
function CaptionText({
  x,
  y,
  text,
  maxWidth,
  allowWrap = false,
}: {
  x: number;
  y: number;
  text: string;
  maxWidth: number;
  allowWrap?: boolean;
}) {
  // ~6px per char at 12px DM Sans medium.
  const charsPerLine = Math.max(1, Math.floor(maxWidth / 6));
  const lines: string[] = [];
  if (!allowWrap) {
    lines.push(text);
  } else {
    let current = "";
    for (const word of text.split(" ")) {
      const candidate = current ? `${current} ${word}` : word;
      if (candidate.length > charsPerLine && current) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }
    if (current) lines.push(current);
  }

  const startY = y - ((lines.length - 1) * CAPTION_LINE_HEIGHT) / 2;
  return (
    <>
      {lines.map((line, index) => (
        <text
          key={line}
          x={x}
          y={startY + index * CAPTION_LINE_HEIGHT}
          dy="0.32em"
          className="fill-withus-dark-blue text-[0.75rem] font-medium"
        >
          {line}
        </text>
      ))}
    </>
  );
}
